<script setup>
import { computed, nextTick, ref } from 'vue';

import MultiselectParametersDropdown from '../../knowledgeBase/components/multiselect/MultiselectParametersDropdown.vue';
import { tokenizePrompt } from '../helpers/promptVariables';

const props = defineProps({
  modelValue: { type: String, default: '' },
  parameters: { type: Array, default: () => [] },
  label: { type: String, default: '' },
  message: { type: String, default: '' },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error'].includes(value),
  },
});

const emit = defineEmits(['update:modelValue', 'blur']);

const textareaRef = ref(null);
const isFocused = ref(false);

const options = computed(() =>
  props.parameters.map(name => ({ name, value: name }))
);

// The trailing space keeps the mirror as tall as the textarea when the prompt ends with a newline.
const segments = computed(() => [
  ...tokenizePrompt(props.modelValue, props.parameters),
  { text: ' ' },
]);

const borderClass = computed(() => {
  if (isFocused.value) return 'border-n-brand';
  if (props.messageType === 'error') {
    return 'border-n-ruby-8 hover:border-n-ruby-9';
  }
  return 'border-n-weak hover:border-n-slate-6';
});

const segmentClass = segment => {
  if (!segment.variable) return '';
  if (segment.valid) return 'rounded bg-n-blue-3 ring-1 ring-n-blue-7';
  return 'rounded bg-n-ruby-3 ring-1 ring-n-ruby-7 underline decoration-wavy decoration-n-ruby-9';
};

const onBlur = event => {
  isFocused.value = false;
  emit('blur', event);
};

const insertParameter = parameter => {
  const input = textareaRef.value;
  const placeholder = `{${parameter.name}}`;
  const start = input.selectionStart;
  const end = input.selectionEnd;

  emit(
    'update:modelValue',
    props.modelValue.slice(0, start) + placeholder + props.modelValue.slice(end)
  );

  nextTick(() => {
    input.focus();
    input.setSelectionRange(
      start + placeholder.length,
      start + placeholder.length
    );
  });
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <label v-if="label" class="mb-0.5 text-sm font-medium text-n-slate-12">
      {{ label }}
    </label>
    <div
      class="relative grid px-3 py-3 ltr:pr-10 rtl:pl-10 transition-all duration-500 ease-in-out border rounded-lg bg-n-alpha-black2"
      :class="borderClass"
    >
      <div
        aria-hidden="true"
        class="col-start-1 row-start-1 min-h-[20rem] text-sm text-transparent whitespace-pre-wrap break-words pointer-events-none select-none"
      >
        <template v-for="(segment, index) in segments" :key="index">
          <span :class="segmentClass(segment)">{{ segment.text }}</span>
        </template>
      </div>
      <textarea
        ref="textareaRef"
        :value="modelValue"
        rows="1"
        spellcheck="false"
        class="col-start-1 row-start-1 h-full w-full !m-0 p-0 !rounded-none !bg-transparent !border-0 !outline !outline-0 resize-none overflow-hidden text-sm text-n-slate-12 whitespace-pre-wrap break-words"
        @input="emit('update:modelValue', $event.target.value)"
        @focus="isFocused = true"
        @blur="onBlur"
      />
      <MultiselectParametersDropdown
        class="absolute ltr:right-0 rtl:left-0 top-0"
        :options="options"
        @add="insertParameter"
      />
    </div>
    <p
      v-if="message"
      class="min-w-0 mt-1 mb-0 text-xs truncate transition-all duration-500 ease-in-out"
      :class="messageType === 'error' ? 'text-n-ruby-9' : 'text-n-slate-11'"
    >
      {{ message }}
    </p>
  </div>
</template>
